/**
 * Black Friday 2025 Cart Manager
 *
 * Manages automatic free gift additions/removals for Black Friday 2025 campaign:
 * - Bundles: Add jibbitz + decals when bundle in cart
 * - Non-bundles (future): 1+ products = jibbitz, 2+ products = decals
 *
 * Hijacks fetch() to intercept Shopify cart API calls
 * Debug mode: Add ?bf2025-debug=true to URL
 */

(function() {
  try {
    // ============================================
    // DEBUG CONFIGURATION
    // ============================================
    const urlParams = new URLSearchParams(window.location.search);
    const DEBUG = urlParams.get('bf2025-debug') === 'true' || true;

    // ============================================
    // CONFIGURATION
    // ============================================
    // Free gift variant IDs
    const FREE_GIFTS = {
      jibbitz: 43643388100652,
      decals: 42756005756972,
      socks: window.BF_SOCKS_VARIANT_IDS || [] // All sock variant IDs (multiple sizes)
    };

    // Store original fetch BEFORE any modifications
    const originalFetch = window.fetch;

    // Cart state tracking
    let lastKnownCart = null;
    let isProcessing = false; // Prevent infinite loops

    // Progress state tracking for GUI
    let progressState = {
      bundleCount: 0,
      productCount: 0,
      hasJibbitz: false,
      hasDecals: false,
      hasSocks: false,
      hasFreePack: false,
      nextThreshold: 'jibbitz' // 'jibbitz', 'decals', 'socks', 'pack', or 'complete'
    };

    // ============================================
    // LOGGER SETUP
    // ============================================
    const createLogger = () => {
      const logLevels = {
        debug: 0,
        info: 1,
        warning: 3,
        success: 4,
        error: 5
      };

      const colors = {
        debug: "#9b59b6",
        info: "#3498db",
        success: "#2ecc71",
        warning: "#f1c40f",
        error: "#e74c3c"
      };

      const currentLogLevel = logLevels.debug; // Log everything at debug level or higher

      const logFn = (type, ...args) => {
        if (DEBUG && logLevels[type] >= currentLogLevel) {
          console.log(
            `%cBF2025-CART`,
            `background: ${colors[type]}; color: white; padding: 2px 5px; border-radius: 3px;`,
            ...args
          );
        }
      };

      return {
        debug: (...args) => logFn("debug", ...args),
        info: (...args) => logFn("info", ...args),
        success: (...args) => logFn("success", ...args),
        warning: (...args) => logFn("warning", ...args),
        error: (...args) => logFn("error", ...args)
      };
    };

    const log = createLogger();

    // ============================================
    // HELPER FUNCTIONS
    // ============================================

    /**
     * Check if cart item is a bundle
     */
    function isBundleItem(item) {
      return item.properties && item.properties._bf_bundle === 'true';
    }

    /**
     * Check if cart item is a free gift
     */
    function isGiftItem(item) {
      return item.properties && item.properties._bf_free_gift === 'true';
    }

    /**
     * Count bundles in cart
     */
    function getBundleCount(cartItems) {
      if (!cartItems) return 0;
      return cartItems.filter(isBundleItem).length;
    }

    /**
     * Get free gift items from cart
     */
    function getGiftItems(cartItems) {
      if (!cartItems) return [];
      return cartItems.filter(isGiftItem);
    }

    /**
     * Check if a specific gift is in cart
     */
    function hasGift(cartItems, variantId) {
      const gifts = getGiftItems(cartItems);
      return gifts.some(item => item.variant_id === variantId);
    }

    /**
     * Check if cart item is a BF product
     */
    function isProductItem(item) {
      return item.properties && item.properties._bf_product === 'true';
    }

    /**
     * Parse pack count from variant title
     * - If title contains "pack/packs" (case-insensitive) and a number 1-4, return that number
     * - Otherwise return 1
     */
    function parsePackCount(variantTitle) {
      // No pack in title → counts as 1
      if (!variantTitle || !/pack/i.test(variantTitle)) {
        return 1;
      }

      // Extract first number from title
      const match = variantTitle.match(/(\d+)/);
      if (!match) {
        return 1;
      }

      const num = parseInt(match[1], 10);

      // Only accept 1-4 range, else default to 1
      if (num >= 1 && num <= 4) {
        return num;
      }

      return 1;
    }

    /**
     * Get total pack-based count of BF products in cart
     * - Parses variant title for pack count (1-4)
     * - Multiplies pack count by quantity
     */
    function getProductCount(cartItems) {
      if (!cartItems) return 0;
      return cartItems
        .filter(isProductItem)
        .reduce((total, item) => {
          const packCount = parsePackCount(item.variant_title);
          return total + (packCount * item.quantity);
        }, 0);
    }

    /**
     * Check if any sock variant is in cart
     */
    function hasSocksGift(cartItems) {
      if (!cartItems || !FREE_GIFTS.socks || FREE_GIFTS.socks.length === 0) return false;
      const gifts = getGiftItems(cartItems);
      return gifts.some(item => FREE_GIFTS.socks.includes(item.variant_id));
    }

    /**
     * Check if a tier 4 free pack is in cart
     * Tier 4 gifts are marked with _bf_free_gift: 'tier4'
     */
    function hasFreePackGift(cartItems) {
      if (!cartItems) return false;
      return cartItems.some(item =>
        item.properties && item.properties._bf_free_gift === 'tier4'
      );
    }

    /**
     * Update progress state based on cart
     */
    function updateProgressState(cart) {
      if (!cart || !cart.items) {
        progressState = {
          bundleCount: 0,
          productCount: 0,
          hasJibbitz: false,
          hasDecals: false,
          hasSocks: false,
          hasFreePack: false,
          nextThreshold: 'jibbitz'
        };
        return;
      }

      const bundleCount = getBundleCount(cart.items);
      const productCount = getProductCount(cart.items);
      // Bundles count as 3 packs each for tier calculations
      const effectivePackCount = productCount + (bundleCount * 3);

      const hasJibbitz = hasGift(cart.items, FREE_GIFTS.jibbitz);
      const hasDecals = hasGift(cart.items, FREE_GIFTS.decals);
      const hasSocks = hasSocksGift(cart.items);
      const hasFreePack = hasFreePackGift(cart.items);

      // Determine next threshold based on effective pack count
      let nextThreshold;
      if (effectivePackCount === 0) {
        nextThreshold = 'jibbitz';
      } else if (effectivePackCount === 1) {
        nextThreshold = 'decals';
      } else if (effectivePackCount === 2) {
        nextThreshold = 'socks';
      } else if (effectivePackCount === 3) {
        nextThreshold = 'pack'; // 4th tier
      } else {
        nextThreshold = 'complete'; // 4+ effective packs = all tiers unlocked
      }

      progressState = {
        bundleCount,
        productCount,
        effectivePackCount,
        hasJibbitz,
        hasDecals,
        hasSocks,
        hasFreePack,
        nextThreshold
      };

      log.debug('updateProgressState:', progressState);
    }

    /**
     * Validate cart and remove orphaned free gifts
     * Called on initialization to clean up cart state
     */
    async function validateAndCleanCart() {
      log.info('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      log.info('validateAndCleanCart: Validating cart state');

      if (!lastKnownCart || !lastKnownCart.items) {
        log.info('validateAndCleanCart: No cart to validate');
        log.info('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        return;
      }

      const bundleCount = getBundleCount(lastKnownCart.items);
      const productCount = getProductCount(lastKnownCart.items);
      // Bundles count as 3 packs each for tier calculations
      const effectivePackCount = productCount + (bundleCount * 3);

      const hasJibbitz = hasGift(lastKnownCart.items, FREE_GIFTS.jibbitz);
      const hasDecals = hasGift(lastKnownCart.items, FREE_GIFTS.decals);
      const hasSocks = hasSocksGift(lastKnownCart.items);
      const hasFreePack = hasFreePackGift(lastKnownCart.items);

      log.info('validateAndCleanCart: Current state:', {
        bundleCount,
        productCount,
        effectivePackCount,
        hasJibbitz,
        hasDecals,
        hasSocks,
        hasFreePack
      });

      // Determine what gifts SHOULD be in cart
      let shouldHaveJibbitz = false;
      let shouldHaveDecals = false;
      let shouldHaveSocks = false;
      let shouldHaveFreePack = false;

      if (bundleCount > 0) {
        // Bundles automatically unlock tiers 1-3
        shouldHaveJibbitz = true;
        shouldHaveDecals = true;
        shouldHaveSocks = true;
        log.info('validateAndCleanCart: Bundles present → should have jibbitz, decals, socks');
      } else {
        if (productCount >= 1) {
          shouldHaveJibbitz = true;
          log.info('validateAndCleanCart: 1+ products → should have jibbitz');
        }
        if (productCount >= 2) {
          shouldHaveDecals = true;
          log.info('validateAndCleanCart: 2+ products → should have decals');
        }
        if (productCount >= 3) {
          shouldHaveSocks = true;
          log.info('validateAndCleanCart: 3+ products → should have socks');
        }
      }

      // Tier 4: Use effective pack count (bundles = 3 packs each)
      if (effectivePackCount >= 4) {
        shouldHaveFreePack = true;
        log.info('validateAndCleanCart: 4+ effective packs → should have free pack');
      }

      const giftsToRemove = [];

      // Check for orphaned gifts
      if (!shouldHaveJibbitz && hasJibbitz) {
        log.warning('validateAndCleanCart: ⚠️  Orphaned jibbitz detected - will remove');
        giftsToRemove.push(FREE_GIFTS.jibbitz);
      }
      if (!shouldHaveDecals && hasDecals) {
        log.warning('validateAndCleanCart: ⚠️  Orphaned decals detected - will remove');
        giftsToRemove.push(FREE_GIFTS.decals);
      }
      if (!shouldHaveSocks && hasSocks) {
        log.warning('validateAndCleanCart: ⚠️  Orphaned socks detected - will remove');
        // Find the actual sock variant in cart and add it to removal list
        const sockItem = lastKnownCart.items.find(item =>
          isGiftItem(item) && FREE_GIFTS.socks.includes(item.variant_id)
        );
        if (sockItem) {
          giftsToRemove.push(sockItem.variant_id);
        }
      }
      if (!shouldHaveFreePack && hasFreePack) {
        log.warning('validateAndCleanCart: ⚠️  Orphaned free pack detected - will remove');
        // Find the tier 4 free pack in cart and add to removal list
        const packItem = lastKnownCart.items.find(item =>
          item.properties && item.properties._bf_free_gift === 'tier4'
        );
        if (packItem) {
          giftsToRemove.push(packItem.variant_id);
        }
      }

      // Remove orphaned gifts
      if (giftsToRemove.length > 0) {
        log.info(`validateAndCleanCart: Removing ${giftsToRemove.length} orphaned gift(s)`);
        isProcessing = true;
        try {
          const removalPayload = buildCombinedRemoval(lastKnownCart, giftsToRemove);
          log.debug('validateAndCleanCart: Removal payload:', removalPayload);

          await originalFetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(removalPayload)
          });

          log.success('validateAndCleanCart: ✅ Orphaned gifts removed successfully');
          await fetchCartState();
        } catch (error) {
          log.error('validateAndCleanCart: Failed to remove orphaned gifts:', error);
        } finally {
          isProcessing = false;
        }
      } else {
        log.info('validateAndCleanCart: ✅ Cart state is valid, no orphaned gifts');
      }

      log.info('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    }

    /**
     * Fetch current cart state
     */
    async function fetchCartState() {
      log.debug('fetchCartState: Fetching cart from /cart.js');
      try {
        const response = await originalFetch('/cart.js');
        const cart = await response.json();
        lastKnownCart = cart;
        updateProgressState(cart);
        log.info('Cart state updated:', {
          itemCount: cart.item_count,
          items: cart.items.map(i => ({
            id: i.variant_id,
            qty: i.quantity,
            props: i.properties
          }))
        });
        return cart;
      } catch (error) {
        log.error('Failed to fetch cart state:', error);
        return null;
      }
    }

    /**
     * Add free gifts to cart
     */
    async function addFreeGifts(giftsToAdd) {
      if (giftsToAdd.length === 0) {
        log.debug('addFreeGifts: No gifts to add');
        return;
      }

      log.info('Adding free gifts:', {
        gifts: giftsToAdd,
        giftNames: giftsToAdd.map(id => {
          if (id === FREE_GIFTS.jibbitz) return 'jibbitz';
          if (id === FREE_GIFTS.decals) return 'decals';
          return 'unknown';
        })
      });

      const items = giftsToAdd.map(variantId => ({
        id: String(variantId),
        quantity: 1,
        properties: {
          _bf_free_gift: 'true'
        }
      }));

      log.debug('addFreeGifts: Payload:', { items });

      try {
        isProcessing = true;
        log.debug('addFreeGifts: Setting isProcessing = true');

        const response = await originalFetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ items })
        });

        const result = await response.json();
        log.success('Free gifts added successfully:', result);

        // Update cart state
        await fetchCartState();

        return result;
      } catch (error) {
        log.error('Failed to add free gifts:', error);
      } finally {
        isProcessing = false;
        log.debug('addFreeGifts: Setting isProcessing = false');
      }
    }

    /**
     * Build combined update payload for removing multiple items
     */
    function buildCombinedRemoval(cart, itemsToRemove) {
      log.debug('buildCombinedRemoval: Building removal payload', { itemsToRemove });
      const updates = {};

      itemsToRemove.forEach(variantId => {
        // Use variant ID as key (Shopify's /cart/update.js expects variant IDs, not variant_id:line format)
        const variantKey = String(variantId);
        updates[variantKey] = 0; // Set quantity to 0 to remove
        log.debug(`buildCombinedRemoval: Adding removal for variant ${variantKey}`);
      });

      log.debug('buildCombinedRemoval: Final payload:', { updates });
      return { updates };
    }

    // ============================================
    // CART OPERATION HANDLERS
    // ============================================

    /**
     * Handle /cart/add.js requests
     */
    async function handleAddToCart(url, options) {
      log.info('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      log.info('handleAddToCart: Intercepted /cart/add.js');
      log.debug('handleAddToCart: Request details:', { url, body: options.body });

      // Execute original request
      const response = await originalFetch(url, options);
      const responseClone = response.clone();
      const responseData = await responseClone.json();

      log.success('handleAddToCart: Item added to cart successfully');
      log.debug('handleAddToCart: Response data:', responseData);

      // Don't process if we're already processing (prevent infinite loop)
      if (isProcessing) {
        log.warning('handleAddToCart: Already processing, skipping gift logic');
        return response;
      }

      // Get current cart state
      log.debug('handleAddToCart: Fetching current cart state');
      const cart = await fetchCartState();
      if (!cart) {
        log.error('handleAddToCart: Failed to get cart state, aborting');
        return response;
      }

      // Check if we need to add free gifts
      const bundleCount = getBundleCount(cart.items);
      const productCount = getProductCount(cart.items);
      const hasJibbitzNow = hasGift(cart.items, FREE_GIFTS.jibbitz);
      const hasDecalsNow = hasGift(cart.items, FREE_GIFTS.decals);

      log.info('handleAddToCart: Cart analysis:', {
        bundleCount,
        productCount,
        hasJibbitz: hasJibbitzNow,
        hasDecals: hasDecalsNow
      });

      const giftsToAdd = [];

      // Priority 1: Bundles give both gifts
      if (bundleCount > 0) {
        log.info('handleAddToCart: Bundles detected → both gifts should be present');
        if (!hasJibbitzNow) {
          log.info('handleAddToCart: Jibbitz missing, will add');
          giftsToAdd.push(FREE_GIFTS.jibbitz);
        }
        if (!hasDecalsNow) {
          log.info('handleAddToCart: Decals missing, will add');
          giftsToAdd.push(FREE_GIFTS.decals);
        }
      }
      // Priority 2: No bundles, check product count
      else {
        log.info('handleAddToCart: No bundles, checking product thresholds');

        // 1+ products → jibbitz
        if (productCount >= 1 && !hasJibbitzNow) {
          log.info('handleAddToCart: Product count ≥ 1, Jibbitz missing, will add');
          giftsToAdd.push(FREE_GIFTS.jibbitz);
        }

        // 2+ products → decals
        if (productCount >= 2 && !hasDecalsNow) {
          log.info('handleAddToCart: Product count ≥ 2, Decals missing, will add');
          giftsToAdd.push(FREE_GIFTS.decals);
        }

        if (productCount === 0) {
          log.info('handleAddToCart: No products, no gifts needed');
        } else if (productCount === 1) {
          log.info('handleAddToCart: 1 product → jibbitz only');
        } else {
          log.info('handleAddToCart: 2+ products → both gifts');
        }
      }

      // Add any missing gifts
      if (giftsToAdd.length > 0) {
        log.info(`handleAddToCart: Adding ${giftsToAdd.length} free gift(s)`);
        await addFreeGifts(giftsToAdd);
      } else {
        log.info('handleAddToCart: All required gifts already in cart');
      }

      // Trigger cart drawer
      if (typeof window.prepareDataForDisplayWidgetByRule === 'function') {
        log.debug('handleAddToCart: Triggering iCart drawer');
        window.prepareDataForDisplayWidgetByRule();
      } else {
        log.debug('handleAddToCart: iCart drawer function not available');
      }

      // Publish cart update event
      if (window.publish && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
        log.debug('handleAddToCart: Publishing cartUpdate event');
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'bf2025-cart-manager'
        });
      }

      log.info('handleAddToCart: Complete');
      log.info('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      return response;
    }

    /**
     * Calculate what the cart will look like after an update
     */
    function simulateCartUpdate(currentCart, requestBody, url) {
      log.debug('simulateCartUpdate: Starting simulation');
      if (!currentCart || !requestBody) {
        log.warning('simulateCartUpdate: Missing cart or request body');
        return null;
      }

      const simulatedItems = [...currentCart.items];
      log.debug('simulateCartUpdate: Current item count:', currentCart.items.length);

      if (url.includes('/cart/change.js')) {
        log.debug('simulateCartUpdate: Simulating /cart/change.js');
        // /cart/change.js updates a single line item
        // Body formats:
        // - { id: "variant_id:line_index" or "variant_id:hash", quantity: X }
        // - { line: N, quantity: X } (iCart format - 1-indexed)
        const { id, line, quantity } = requestBody;
        log.debug('simulateCartUpdate: Change request:', { id, line, quantity });

        let itemIndex = -1;

        if (line !== undefined) {
          // iCart format: line number (1-indexed)
          itemIndex = line - 1; // Convert to 0-indexed
          log.debug(`simulateCartUpdate: Using line number ${line} (index ${itemIndex})`);
        } else if (id) {
          // Standard format: variant_id:hash or variant_id:index
          const variantId = parseInt(id.split(':')[0]);

          itemIndex = simulatedItems.findIndex((item, idx) => {
            // Try exact match first
            const lineKey = `${item.variant_id}:${idx + 1}`;
            if (lineKey === id) return true;

            // Fallback: match by variant_id (for hash format)
            return item.variant_id === variantId;
          });
        }

        if (itemIndex !== -1 && itemIndex < simulatedItems.length) {
          if (quantity === 0) {
            log.debug(`simulateCartUpdate: Removing item at index ${itemIndex}`);
            simulatedItems.splice(itemIndex, 1); // Remove item
          } else {
            log.debug(`simulateCartUpdate: Updating quantity to ${quantity} for item at index ${itemIndex}`);
            simulatedItems[itemIndex].quantity = quantity;
          }
        } else {
          log.warning(`simulateCartUpdate: Item not found (id: ${id}, line: ${line})`);
        }
      } else if (url.includes('/cart/update.js')) {
        log.debug('simulateCartUpdate: Simulating /cart/update.js');
        // /cart/update.js can update multiple items
        // Body format: { updates: { "variant_id:line_index" or "variant_id:hash": quantity, ... } }
        const { updates } = requestBody;
        log.debug('simulateCartUpdate: Updates:', updates);

        if (updates) {
          // Process removals in reverse order to handle index shifting
          const updateEntries = Object.entries(updates);

          updateEntries.forEach(([lineKey, quantity]) => {
            // Extract variant_id from line key (handles both formats)
            const variantId = parseInt(lineKey.split(':')[0]);

            log.debug(`simulateCartUpdate: Looking for variant ${variantId} from lineKey ${lineKey}`);
            log.debug(`simulateCartUpdate: Current cart items:`, simulatedItems.map((item, idx) => ({
              idx,
              variant_id: item.variant_id,
              type: typeof item.variant_id,
              matches: item.variant_id === variantId,
              properties: item.properties
            })));

            const itemIndex = simulatedItems.findIndex((item, idx) => {
              // Try exact match first
              const key = `${item.variant_id}:${idx + 1}`;
              if (key === lineKey) return true;

              // Fallback: match by variant_id (for iCart hash format)
              return item.variant_id === variantId;
            });

            if (itemIndex !== -1) {
              if (quantity === 0) {
                log.debug(`simulateCartUpdate: Removing item at index ${itemIndex} (${lineKey}, variant ${variantId})`);
                simulatedItems.splice(itemIndex, 1); // Remove item
              } else {
                log.debug(`simulateCartUpdate: Updating quantity to ${quantity} for ${lineKey}`);
                simulatedItems[itemIndex].quantity = quantity;
              }
            } else {
              log.warning(`simulateCartUpdate: Line key ${lineKey} (variant ${variantId}) not found in cart`);
            }
          });
        }
      }

      log.debug('simulateCartUpdate: Simulated item count:', simulatedItems.length);
      return { items: simulatedItems };
    }

    /**
     * Handle /cart/change.js and /cart/update.js requests
     */
    async function handleCartUpdate(url, options) {
      log.info('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      log.info('handleCartUpdate: Intercepted cart update/change');
      log.debug('handleCartUpdate: URL:', url);
      log.debug('handleCartUpdate: Options:', options);

      // Don't process if we're already processing
      if (isProcessing) {
        log.debug('handleCartUpdate: Already processing, passing through');
        return originalFetch(url, options);
      }

      // We need to check if this update will remove bundles
      log.debug('handleCartUpdate: Getting current cart state');
      const currentCart = lastKnownCart || await fetchCartState();
      if (!currentCart) {
        log.warning('handleCartUpdate: No cart state available, passing through');
        return originalFetch(url, options);
      }

      // Parse the request body
      let requestBody;
      try {
        requestBody = options.body ? JSON.parse(options.body) : null;
        log.info('handleCartUpdate: Request body:', requestBody);
      } catch (error) {
        log.error('handleCartUpdate: Failed to parse request body:', error);
        return originalFetch(url, options);
      }

      // Simulate what the cart will look like after this update
      log.debug('handleCartUpdate: Simulating cart update');
      const simulatedCart = simulateCartUpdate(currentCart, requestBody, url);
      const currentBundleCount = getBundleCount(currentCart.items);
      const simulatedBundleCount = simulatedCart ? getBundleCount(simulatedCart.items) : currentBundleCount;

      log.info('handleCartUpdate: Bundle count comparison:', {
        current: currentBundleCount,
        simulated: simulatedBundleCount
      });

      // Check if we're removing the last bundle(s)
      if (currentBundleCount > 0 && simulatedBundleCount === 0) {
        // This update will remove all bundles - combine bundle removal + gift removal
        log.warning('handleCartUpdate: ⚠️  LAST BUNDLE BEING REMOVED - COMBINING WITH GIFT REMOVAL');

        const giftItems = getGiftItems(currentCart.items);
        log.info(`handleCartUpdate: Found ${giftItems.length} gift(s) to remove`);

        if (giftItems.length > 0) {
          // Build combined removal: original updates + gift removals
          isProcessing = true;
          log.debug('handleCartUpdate: Setting isProcessing = true');

          try {
            let combinedPayload;

            if (url.includes('/cart/update.js')) {
              log.debug('handleCartUpdate: Using /cart/update.js, adding to existing updates');
              // Add gift removals to existing updates
              combinedPayload = {
                updates: {
                  ...requestBody.updates
                }
              };

              // Add gift removals
              giftItems.forEach((item) => {
                const lineKey = `${item.variant_id}:${currentCart.items.indexOf(item) + 1}`;
                combinedPayload.updates[lineKey] = 0;
                log.debug(`handleCartUpdate: Adding gift removal ${lineKey}`);
              });
            } else {
              log.debug('handleCartUpdate: Converting /cart/change.js to /cart/update.js');
              // Convert /cart/change.js to /cart/update.js with multiple removals
              url = '/cart/update.js';
              combinedPayload = { updates: {} };

              // Add original removal
              if (requestBody.id) {
                combinedPayload.updates[requestBody.id] = requestBody.quantity || 0;
                log.debug(`handleCartUpdate: Adding original removal ${requestBody.id}`);
              }

              // Add gift removals
              giftItems.forEach((item) => {
                const lineKey = `${item.variant_id}:${currentCart.items.indexOf(item) + 1}`;
                combinedPayload.updates[lineKey] = 0;
                log.debug(`handleCartUpdate: Adding gift removal ${lineKey}`);
              });
            }

            log.info('handleCartUpdate: Combined removal payload:', combinedPayload);

            // Execute combined removal
            log.debug('handleCartUpdate: Executing combined removal');
            const response = await originalFetch(url, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(combinedPayload)
            });

            const responseClone = response.clone();
            const responseData = await responseClone.json();

            log.success('handleCartUpdate: ✅ Combined removal executed successfully');
            log.debug('handleCartUpdate: Response data:', responseData);

            // Update cart state
            log.debug('handleCartUpdate: Updating cart state after combined removal');
            await fetchCartState();

            log.info('handleCartUpdate: Complete (combined removal)');
            log.info('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
            return response;
          } catch (error) {
            log.error('handleCartUpdate: Failed to execute combined removal:', error);
            // Fallback to original request
            return originalFetch(url, options);
          } finally {
            isProcessing = false;
            log.debug('handleCartUpdate: Setting isProcessing = false');
          }
        } else {
          log.info('handleCartUpdate: No gifts to remove, executing original request');
        }
      }

      // Not removing last bundle - execute original request
      log.debug('handleCartUpdate: Not removing last bundle, executing original request');
      const response = await originalFetch(url, options);
      const responseClone = response.clone();
      const responseData = await responseClone.json();

      log.success('handleCartUpdate: Cart updated successfully');
      log.debug('handleCartUpdate: Response data:', responseData);

      // Get new cart state
      log.debug('handleCartUpdate: Fetching new cart state');
      const newCart = await fetchCartState();
      if (!newCart) {
        log.warning('handleCartUpdate: Failed to get new cart state');
        return response;
      }

      // Re-evaluate gifts based on new cart state
      const newBundleCount = getBundleCount(newCart.items);
      const newProductCount = getProductCount(newCart.items);
      // Bundles count as 3 packs each for tier calculations
      const newEffectivePackCount = newProductCount + (newBundleCount * 3);

      const hasJibbitzNow = hasGift(newCart.items, FREE_GIFTS.jibbitz);
      const hasDecalsNow = hasGift(newCart.items, FREE_GIFTS.decals);
      const hasFreePackNow = hasFreePackGift(newCart.items);

      log.info('handleCartUpdate: New cart analysis:', {
        bundleCount: newBundleCount,
        productCount: newProductCount,
        effectivePackCount: newEffectivePackCount,
        hasJibbitz: hasJibbitzNow,
        hasDecals: hasDecalsNow,
        hasFreePack: hasFreePackNow
      });

      const giftsToAdd = [];
      const giftsToRemove = [];

      // Determine what gifts should be present
      let shouldHaveJibbitz = false;
      let shouldHaveDecals = false;
      let shouldHaveSocks = false;
      let shouldHaveFreePack = false;

      if (newBundleCount > 0) {
        log.info('handleCartUpdate: Bundles present → jibbitz, decals, socks should exist');
        shouldHaveJibbitz = true;
        shouldHaveDecals = true;
        shouldHaveSocks = true;
      } else {
        log.info('handleCartUpdate: No bundles, checking product thresholds');
        if (newProductCount >= 1) {
          shouldHaveJibbitz = true;
          log.debug('handleCartUpdate: Product count ≥ 1 → jibbitz should exist');
        }
        if (newProductCount >= 2) {
          shouldHaveDecals = true;
          log.debug('handleCartUpdate: Product count ≥ 2 → decals should exist');
        }
        if (newProductCount >= 3) {
          shouldHaveSocks = true;
          log.debug('handleCartUpdate: Product count ≥ 3 → socks should exist');
        }
      }

      // Tier 4: Use effective pack count (bundles = 3 packs each)
      if (newEffectivePackCount >= 4) {
        shouldHaveFreePack = true;
        log.debug('handleCartUpdate: Effective pack count ≥ 4 → free pack should exist');
      }

      const hasSocksNow = hasSocksGift(newCart.items);

      // Compare current state with desired state
      if (shouldHaveJibbitz && !hasJibbitzNow) {
        log.info('handleCartUpdate: Jibbitz should be present but is missing, will add');
        giftsToAdd.push(FREE_GIFTS.jibbitz);
      } else if (!shouldHaveJibbitz && hasJibbitzNow) {
        log.warning('handleCartUpdate: Jibbitz should NOT be present but exists, will remove');
        giftsToRemove.push(FREE_GIFTS.jibbitz);
      }

      if (shouldHaveDecals && !hasDecalsNow) {
        log.info('handleCartUpdate: Decals should be present but are missing, will add');
        giftsToAdd.push(FREE_GIFTS.decals);
      } else if (!shouldHaveDecals && hasDecalsNow) {
        log.warning('handleCartUpdate: Decals should NOT be present but exist, will remove');
        giftsToRemove.push(FREE_GIFTS.decals);
      }

      // Check socks
      if (!shouldHaveSocks && hasSocksNow) {
        log.warning('handleCartUpdate: Socks should NOT be present but exist, will remove');
        const sockItem = newCart.items.find(item =>
          isGiftItem(item) && FREE_GIFTS.socks.includes(item.variant_id)
        );
        if (sockItem) {
          giftsToRemove.push(sockItem.variant_id);
        }
      }

      // Check tier 4 free pack
      if (!shouldHaveFreePack && hasFreePackNow) {
        log.warning('handleCartUpdate: Free pack should NOT be present but exists, will remove');
        // Find the tier 4 free pack variant ID
        const packItem = newCart.items.find(item =>
          item.properties && item.properties._bf_free_gift === 'tier4'
        );
        if (packItem) {
          giftsToRemove.push(packItem.variant_id);
        }
      }

      // Add missing gifts
      if (giftsToAdd.length > 0) {
        log.info(`handleCartUpdate: Adding ${giftsToAdd.length} missing gift(s)`);
        await addFreeGifts(giftsToAdd);
        // addFreeGifts() calls fetchCartState() which updates lastKnownCart

        // Use the updated lastKnownCart and recalculate what needs to be removed
        log.debug('handleCartUpdate: Using updated lastKnownCart to recalculate removals');
        if (lastKnownCart) {
          // Recalculate giftsToRemove based on updated cart
          giftsToRemove.length = 0; // Clear the array
          const hasJibbitzAfterAdd = hasGift(lastKnownCart.items, FREE_GIFTS.jibbitz);
          const hasDecalsAfterAdd = hasGift(lastKnownCart.items, FREE_GIFTS.decals);

          if (!shouldHaveJibbitz && hasJibbitzAfterAdd) {
            log.debug('handleCartUpdate: After adding, jibbitz should still be removed');
            giftsToRemove.push(FREE_GIFTS.jibbitz);
          }
          if (!shouldHaveDecals && hasDecalsAfterAdd) {
            log.debug('handleCartUpdate: After adding, decals should still be removed');
            giftsToRemove.push(FREE_GIFTS.decals);
          }
        }
      }

      // Remove extra gifts
      if (giftsToRemove.length > 0) {
        log.info(`handleCartUpdate: Removing ${giftsToRemove.length} gift(s) that shouldn't be present`);
        isProcessing = true;
        try {
          // Use lastKnownCart which is always current (updated by our interceptor)
          log.debug('handleCartUpdate: Using lastKnownCart for removal payload');
          if (!lastKnownCart) {
            log.error('handleCartUpdate: lastKnownCart is null, aborting removal');
            return response;
          }

          const removalPayload = buildCombinedRemoval(lastKnownCart, giftsToRemove);
          log.debug('handleCartUpdate: Gift removal payload:', removalPayload);

          await originalFetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(removalPayload)
          });

          log.success('handleCartUpdate: Gifts removed successfully');
          await fetchCartState();
        } catch (error) {
          log.error('handleCartUpdate: Failed to remove gifts:', error);
        } finally {
          isProcessing = false;
        }
      }

      if (giftsToAdd.length === 0 && giftsToRemove.length === 0) {
        log.info('handleCartUpdate: Gift state is correct, no changes needed');
      }

      log.info('handleCartUpdate: Complete');
      log.info('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      return response;
    }

    /**
     * Handle /cart.js requests (passive monitoring)
     */
    async function handleGetCart(url, options) {
      log.debug('handleGetCart: Intercepted /cart.js (passive monitoring)');
      const response = await originalFetch(url, options);
      const responseClone = response.clone();

      try {
        const cart = await responseClone.json();
        lastKnownCart = cart;
        log.info('handleGetCart: Cart state tracked:', {
          itemCount: cart.item_count,
          items: cart.items.map(i => ({
            id: i.variant_id,
            qty: i.quantity,
            props: i.properties
          }))
        });
      } catch (error) {
        log.error('handleGetCart: Failed to parse cart response:', error);
      }

      return response;
    }

    /**
     * Handle /cart/clear.js requests
     */
    async function handleClearCart(url, options) {
      log.info('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      log.warning('handleClearCart: Cart being cleared');
      const response = await originalFetch(url, options);
      lastKnownCart = null;
      log.info('handleClearCart: lastKnownCart reset to null');
      log.info('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      return response;
    }

    // ============================================
    // FETCH HIJACKING
    // ============================================

    // Hijack XMLHttpRequest for older cart implementations (like iCart)
    const OriginalXHR = window.XMLHttpRequest;
    window.XMLHttpRequest = function() {
      const xhr = new OriginalXHR();
      const originalOpen = xhr.open;
      const originalSend = xhr.send;
      let xhrUrl = '';
      let xhrMethod = '';
      let xhrBody = null;

      xhr.open = function(method, url, ...args) {
        xhrUrl = url;
        xhrMethod = method.toUpperCase();
        // Don't log here - only log if we actually intercept
        return originalOpen.apply(this, [method, url, ...args]);
      };

      xhr.send = async function(body) {
        xhrBody = body;

        // Only intercept cart update/change calls
        if (xhrMethod === 'POST' && typeof xhrUrl === 'string') {
          if (xhrUrl.includes('/cart/update.js') || xhrUrl.includes('/cart/change.js')) {

            // Don't process if we're already processing
            if (isProcessing) {
              log.debug('XHR: Already processing, passing through');
              return originalSend.apply(this, [body]);
            }

            log.info(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
            log.info(`XHR intercepted: ${xhrMethod} ${xhrUrl}`);

            // Parse the request body
            let requestBody;
            try {
              requestBody = body ? JSON.parse(body) : null;
              log.info('XHR request body:', requestBody);
            } catch (error) {
              log.error('XHR: Failed to parse request body, passing through');
              return originalSend.apply(this, [body]);
            }

            // Get current cart state
            const currentCart = lastKnownCart || await fetchCartState();
            if (!currentCart) {
              log.warning('XHR: No cart state available, passing through');
              return originalSend.apply(this, [body]);
            }

            // Simulate what the cart will look like after this update
            const simulatedCart = simulateCartUpdate(currentCart, requestBody, xhrUrl);
            const currentBundleCount = getBundleCount(currentCart.items);
            const currentProductCount = getProductCount(currentCart.items);
            const simulatedBundleCount = simulatedCart ? getBundleCount(simulatedCart.items) : currentBundleCount;
            const simulatedProductCount = simulatedCart ? getProductCount(simulatedCart.items) : currentProductCount;
            // Calculate effective pack counts (bundles = 3 packs each)
            const currentEffectivePackCount = currentProductCount + (currentBundleCount * 3);
            const simulatedEffectivePackCount = simulatedProductCount + (simulatedBundleCount * 3);

            log.info('XHR: Comparison:', {
              currentBundles: currentBundleCount,
              simulatedBundles: simulatedBundleCount,
              currentProducts: currentProductCount,
              simulatedProducts: simulatedProductCount,
              currentEffectivePacks: currentEffectivePackCount,
              simulatedEffectivePacks: simulatedEffectivePackCount
            });

            // Check if we need to remove gifts
            let needsCombinedRemoval = false;
            const giftsToRemove = [];

            // Check if removing last bundle(s)
            if (currentBundleCount > 0 && simulatedBundleCount === 0) {
              log.warning('XHR: ⚠️  LAST BUNDLE BEING REMOVED');
              needsCombinedRemoval = true;
              const giftItems = getGiftItems(currentCart.items);
              giftItems.forEach(item => giftsToRemove.push(item.variant_id));
            }
            // Check if product count drops below thresholds
            else {
              // Determine what gifts should exist after update
              let shouldHaveJibbitz = false;
              let shouldHaveDecals = false;
              let shouldHaveSocks = false;
              let shouldHaveFreePack = false;

              if (simulatedBundleCount > 0) {
                shouldHaveJibbitz = true;
                shouldHaveDecals = true;
                shouldHaveSocks = true;
              } else {
                if (simulatedProductCount >= 1) shouldHaveJibbitz = true;
                if (simulatedProductCount >= 2) shouldHaveDecals = true;
                if (simulatedProductCount >= 3) shouldHaveSocks = true;
              }

              // Tier 4: Use effective pack count
              if (simulatedEffectivePackCount >= 4) {
                shouldHaveFreePack = true;
              }

              const hasJibbitz = hasGift(currentCart.items, FREE_GIFTS.jibbitz);
              const hasDecals = hasGift(currentCart.items, FREE_GIFTS.decals);
              const hasSocks = hasSocksGift(currentCart.items);
              const hasFreePack = hasFreePackGift(currentCart.items);

              if (!shouldHaveJibbitz && hasJibbitz) {
                log.warning('XHR: Jibbitz should be removed');
                needsCombinedRemoval = true;
                giftsToRemove.push(FREE_GIFTS.jibbitz);
              }
              if (!shouldHaveDecals && hasDecals) {
                log.warning('XHR: Decals should be removed');
                needsCombinedRemoval = true;
                giftsToRemove.push(FREE_GIFTS.decals);
              }
              // Check socks
              if (!shouldHaveSocks && hasSocks) {
                log.warning('XHR: Socks should be removed');
                needsCombinedRemoval = true;
                const sockItem = currentCart.items.find(item =>
                  isGiftItem(item) && FREE_GIFTS.socks.includes(item.variant_id)
                );
                if (sockItem) {
                  giftsToRemove.push(sockItem.variant_id);
                }
              }
              // Check tier 4 free pack
              if (!shouldHaveFreePack && hasFreePack) {
                log.warning('XHR: Free pack should be removed');
                needsCombinedRemoval = true;
                const packItem = currentCart.items.find(item =>
                  item.properties && item.properties._bf_free_gift === 'tier4'
                );
                if (packItem) {
                  giftsToRemove.push(packItem.variant_id);
                }
              }
            }

            // If we need to remove gifts, let original request complete then remove gifts
            if (needsCombinedRemoval && giftsToRemove.length > 0) {
              log.info(`XHR: ⚠️  WILL REMOVE ${giftsToRemove.length} GIFT(S) AFTER REQUEST COMPLETES`);

              // Setup completion handler
              const originalOnReadyStateChange = xhr.onreadystatechange;
              xhr.onreadystatechange = async function() {
                // Call original handler first
                if (originalOnReadyStateChange) {
                  originalOnReadyStateChange.apply(this, arguments);
                }

                // After request completes successfully, remove gifts
                if (xhr.readyState === 4 && xhr.status >= 200 && xhr.status < 300) {
                  log.success('XHR: Original request completed, now removing gifts');
                  isProcessing = true;

                  try {
                    // Remove gifts one at a time, refetching cart each time to get current line positions
                    for (const variantId of giftsToRemove) {
                      // Fetch fresh cart before each removal (line positions shift after each removal)
                      const freshCart = await fetchCartState();
                      if (!freshCart) {
                        log.error('XHR: Failed to get fresh cart');
                        continue;
                      }

                      const item = freshCart.items.find(i => i.variant_id === variantId);
                      if (item) {
                        const lineNumber = freshCart.items.indexOf(item) + 1;
                        log.debug(`XHR: Removing gift at line ${lineNumber} (variant ${variantId})`);

                        await originalFetch('/cart/change.js', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ line: lineNumber, quantity: 0 })
                        });
                      }
                    }

                    log.success('XHR: ✅ Gifts removed successfully');
                    await fetchCartState();

                    // Refresh iCart UI
                    if (typeof window.prepareDataForDisplayWidgetByRule === 'function') {
                      log.debug('XHR: Refreshing iCart UI');
                      window.prepareDataForDisplayWidgetByRule();
                    }
                  } catch (error) {
                    log.error('XHR: Failed to remove gifts:', error);
                  } finally {
                    isProcessing = false;
                    log.info(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
                  }
                }
              };
            } else {
              log.info('XHR: No gift removal needed, executing original request');

              // Setup completion handler for adding missing gifts
              const handleCompletion = async () => {
                if (xhr.readyState === 4 && xhr.status >= 200 && xhr.status < 300) {
                  log.success('XHR: Cart update completed');
                  const newCart = await fetchCartState();

                  if (newCart) {
                    const newBundleCount = getBundleCount(newCart.items);
                    const newProductCount = getProductCount(newCart.items);
                    const hasJibbitz = hasGift(newCart.items, FREE_GIFTS.jibbitz);
                    const hasDecals = hasGift(newCart.items, FREE_GIFTS.decals);

                    let shouldHaveJibbitz = false;
                    let shouldHaveDecals = false;

                    if (newBundleCount > 0) {
                      shouldHaveJibbitz = true;
                      shouldHaveDecals = true;
                    } else {
                      if (newProductCount >= 1) shouldHaveJibbitz = true;
                      if (newProductCount >= 2) shouldHaveDecals = true;
                    }

                    const giftsToAdd = [];
                    if (shouldHaveJibbitz && !hasJibbitz) giftsToAdd.push(FREE_GIFTS.jibbitz);
                    if (shouldHaveDecals && !hasDecals) giftsToAdd.push(FREE_GIFTS.decals);

                    if (giftsToAdd.length > 0) {
                      log.info(`XHR: Adding ${giftsToAdd.length} missing gift(s)`);
                      await addFreeGifts(giftsToAdd);

                      // Refresh iCart UI
                      if (typeof window.prepareDataForDisplayWidgetByRule === 'function') {
                        log.debug('XHR: Refreshing iCart UI');
                        window.prepareDataForDisplayWidgetByRule();
                      }
                    }
                  }

                  log.info(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
                }
              };

              xhr.onreadystatechange = handleCompletion;
            }
          }
        }

        return originalSend.apply(this, [body]);
      };

      return xhr;
    };

    window.fetch = async function(url, options = {}) {
      // Only intercept cart API calls
      if (typeof url !== 'string') {
        log.debug('fetch: Non-string URL, passing through');
        return originalFetch(url, options);
      }

      try {
        // Route to appropriate handler
        if (url.includes('/cart/add.js')) {
          log.debug('fetch: Routing to handleAddToCart');
          return await handleAddToCart(url, options);
        } else if (url.includes('/cart/change.js') || url.includes('/cart/update.js')) {
          log.debug('fetch: Routing to handleCartUpdate');
          return await handleCartUpdate(url, options);
        } else if (url.includes('/cart.js')) {
          log.debug('fetch: Routing to handleGetCart');
          return await handleGetCart(url, options);
        } else if (url.includes('/cart/clear.js')) {
          log.debug('fetch: Routing to handleClearCart');
          return await handleClearCart(url, options);
        } else {
          // Not a cart API call, pass through
          // log.debug('fetch: Not a cart API call, passing through:', url);
          return originalFetch(url, options);
        }
      } catch (error) {
        log.error('fetch: Critical error in fetch interception:', error);
        log.error('fetch: Falling back to original fetch');
        // Fallback to original fetch on error
        return originalFetch(url, options);
      }
    };

    // ============================================
    // INITIALIZATION
    // ============================================

    log.info('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    log.info('🎄 Black Friday 2025 Cart Manager');
    log.info('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    log.info('Configuration:', {
      debug: DEBUG,
      freeGifts: {
        jibbitz: FREE_GIFTS.jibbitz,
        decals: FREE_GIFTS.decals
      }
    });

    // ============================================
    // EXPOSE API TO WINDOW FOR GUI
    // ============================================

    window.BF2025CartProgress = {
      /**
       * Get current progress state
       * @returns {Object} Progress state object
       */
      getState: () => {
        return { ...progressState };
      },

      /**
       * Get detailed cart info
       * @returns {Object} Cart details
       */
      getCartInfo: () => {
        if (!lastKnownCart) return null;
        const bundleCount = getBundleCount(lastKnownCart.items);
        const productCount = getProductCount(lastKnownCart.items);
        return {
          bundleCount,
          productCount,
          effectivePackCount: productCount + (bundleCount * 3),
          hasJibbitz: hasGift(lastKnownCart.items, FREE_GIFTS.jibbitz),
          hasDecals: hasGift(lastKnownCart.items, FREE_GIFTS.decals),
          hasSocks: hasSocksGift(lastKnownCart.items),
          hasFreePack: hasFreePackGift(lastKnownCart.items),
          totalItems: lastKnownCart.item_count
        };
      },

      /**
       * Get raw cart data
       * @returns {Object} Raw cart object
       */
      getCart: () => {
        return lastKnownCart ? { ...lastKnownCart } : null;
      },

      /**
       * Check if socks size selector form should be shown
       * @returns {Boolean} True if form should be shown
       */
      shouldShowSocksForm: () => {
        if (!lastKnownCart) return false;

        const bundleCount = getBundleCount(lastKnownCart.items);
        const productCount = getProductCount(lastKnownCart.items);
        const hasSocks = hasSocksGift(lastKnownCart.items);

        // Show form if:
        // - User qualifies (3+ products OR bundle)
        // - AND doesn't already have socks in cart
        const qualifies = bundleCount > 0 || productCount >= 3;
        return qualifies && !hasSocks;
      },

      /**
       * Check if pack selector form should be shown (tier 4)
       * @returns {Boolean} True if form should be shown
       */
      shouldShowPackForm: () => {
        // Check feature flag first
        if (!window.BF_TIER4_ENABLED) return false;

        if (!lastKnownCart) return false;

        const bundleCount = getBundleCount(lastKnownCart.items);
        const productCount = getProductCount(lastKnownCart.items);
        // Bundles count as 3 packs each for tier 4 calculation
        const effectivePackCount = productCount + (bundleCount * 3);

        const hasSocks = hasSocksGift(lastKnownCart.items);
        const hasFreePack = hasFreePackGift(lastKnownCart.items);

        // Show form if:
        // - User qualifies (4+ effective packs, where bundles = 3 packs)
        // - AND already has socks (tier 3 complete)
        // - AND doesn't already have free pack
        const qualifies = effectivePackCount >= 4;
        return qualifies && hasSocks && !hasFreePack;
      },

      /**
       * Check if tier 4 feature is enabled
       * @returns {Boolean} True if tier 4 is enabled
       */
      isTier4Enabled: () => {
        return window.BF_TIER4_ENABLED === true;
      }
    };

    log.info('Window API exposed: window.BF2025CartProgress');

    // Fetch initial cart state
    log.debug('Fetching initial cart state...');
    fetchCartState().then(async () => {
      log.success('✅ Black Friday 2025 Cart Manager initialized successfully');
      log.info('Initial cart state:', {
        itemCount: lastKnownCart?.item_count || 0,
        bundles: lastKnownCart ? getBundleCount(lastKnownCart.items) : 0,
        products: lastKnownCart ? getProductCount(lastKnownCart.items) : 0,
        gifts: lastKnownCart ? getGiftItems(lastKnownCart.items).length : 0
      });
      log.info('Progress state:', progressState);
      log.info('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

      // Validate cart and remove orphaned gifts
      await validateAndCleanCart();

      if (DEBUG) {
        log.warning('⚠️  DEBUG MODE ENABLED');
        log.info('To disable debug logs, remove ?bf2025-debug=true from URL');
        log.info('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      }
    });

  } catch (error) {
    console.error('BF2025-CART: Critical error in cart manager. Script disabled.', error);
  }
})();
